import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import ReactPixel from 'react-facebook-pixel';
import { CategoryInterface, IMap, Order, Payment, ProductInterface } from '../services/exports/Interfaces';
import { CompleteBasketItem } from './BasketProvider';
import * as amplitude from '@amplitude/analytics-browser';
import mixpanel from 'mixpanel-browser';
import { v4 as uuidv4 } from 'uuid';
import useLocalStorage, { LocalStorageReservedKey } from '../hooks/global/useLocalStorage';
import {
  ENVIRONMENTS,
  OTP_DELIVERY_CHANNELS,
  OTP_VERIFICATION_FAILURE_REASON,
  PaymentMethod,
} from '../services/exports/Constants';
import { CompiledProduct } from './MenuProvider';
import useDeals from '../hooks/deals/useDeals';
import collect from 'collect.js';
import { selectAppliedOffers } from '../store/slices/OfferSlice';
import {
  DealEventProps,
  OfferEventType,
  PENDING_BONUS_KEY,
  bonusEventProps,
  offerEventProps,
} from '../utils/analytics/dealEventProps';
import { DEDUPE_NAMESPACES, markOnce } from '../utils/analytics/sessionDedupe';
import { LoginBonusKind } from '../services/exports/Constants';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import { getReplayConfig, shouldRecordPercent } from '../services/utilities/replayConfig';
import { disableSentryReplay, enableSentryReplay } from '../services/helpers/SentryReplay';
import { PLATFORM_PROJECT_UTM_CONFIG, resolveMixpanelProject } from '../services/utilities/mixpanelProject';
import { applyConsumerSuperProperties, buildConsumerSuperProperties } from '../utils/analytics/superProperties';
import { AnalyticsPaymentGateway, resolveAnalyticsPaymentGateway } from '../utils/analytics/paymentGateway';
import {
  PLATFORM_PROJECT_CONSENT_CONFIG,
  applyPlatformConsent,
  isConsentGated,
} from '../utils/analytics/platformConsent';
import {
  URL_DERIVED_PROPERTY_BLACKLIST,
  sanitizeInitialReferrer,
  scrubUrlsBeforeSend,
  scrubUrlsBeforeTrack,
} from '../utils/analytics/scrubPersonalData';
import { CookiesConsent, hasTrackingConsent, readCookieConsent } from '../utils/cookieConsent';

export enum TrackableEvent {
  ViewMenu = 'view_menu',
  ViewItem = 'view_item',
  AddToBasket = 'add_to_basket',
  AddToBasketFailed = 'add_to_basket_failed',
  RemoveFromBasket = 'remove_from_basket',
  BeginCheckout = 'begin_checkout',
  BeginPurchase = 'begin_purchase',
  CompletePurchase = 'complete_purchase',

  // Offer funnel
  OfferImpression = 'offer_impression',
  OfferClicked = 'offer_clicked',
  OfferApplied = 'offer_applied',

  // OTP authentication
  OtpRequested = 'otp_requested',
  OtpResendRequested = 'otp_resend_requested',
  OtpVerificationFailed = 'otp_verification_failed',
  OtpVerified = 'otp_verified',

  // Google Analytics
  GA_Conversion = 'conversion',

  // Facebook Pixel
  FB_Purchase = 'Purchase',
}

type Props = {
  children: any;
};

export interface BeginPurchaseExtra {
  payment_method?: PaymentMethod;
  /**
   * The gateway that will process the charge, resolved by the caller from the method the order is
   * charged with (see resolveAnalyticsPaymentGateway). Defaults to resolving `payment_method`.
   */
  payment_gateway?: null | AnalyticsPaymentGateway;
}

export type AnalyticsContextType = {
  trackViewItem: (product: ProductInterface) => void;
  trackAddToBasket: (item: CompleteBasketItem) => void;
  trackAddToBasketFailed: (params: { reason: string; product_id?: number; message?: string }) => void;
  trackRemoveFromBasket: (item: CompleteBasketItem) => void;
  trackBeginCheckout: (order: Order, lineItems: CompleteBasketItem[]) => void;
  trackBeginPurchase: (order: Order, lineItems: CompleteBasketItem[], extra?: BeginPurchaseExtra) => void;
  trackCompletePurchase: (payment: Payment, order: Order) => void;
  trackViewMenu: (menuCategories: IMap<CategoryInterface>) => void;
  trackOtpRequested: (params: { otp_channel: OTP_DELIVERY_CHANNELS; channel_is_default: boolean }) => void;
  trackOtpResendRequested: (params: {
    otp_channel: OTP_DELIVERY_CHANNELS;
    switched_on_resend: boolean;
    resend_number: number;
  }) => void;
  trackOtpVerificationFailed: (params: {
    otp_channel: OTP_DELIVERY_CHANNELS;
    failure_reason: OTP_VERIFICATION_FAILURE_REASON;
    raw_error_code?: string | null;
    status?: number | null;
  }) => void;
  trackOtpVerified: (params: { otp_channel: OTP_DELIVERY_CHANNELS }) => void;
  trackOfferImpression: (props: DealEventProps) => void;
  trackOfferClicked: (props: DealEventProps) => void;
  trackOfferApplied: (props: DealEventProps) => void;
};

export const AnalyticsContext = React.createContext<AnalyticsContextType>(null);

const MP_DISTINCT_ID_KEY = 'mp_distinct_id';
const IS_VIEW_MENU_TRACKED_KEY = 'is_view_storefront_tracked_';
const GTAG_SCRIPT_SRC = 'https://www.googletagmanager.com/gtag/js';

// gtag.js used to be a <script> in index.html, which ran before any consent check could. It is now
// injected only once the visitor accepts. The inline `window.gtag` stub in index.html stays: it only
// queues onto `dataLayer`, and gtag.js replays that queue when it loads.
function loadGtagScript(): void {
  if (document.querySelector(`script[src^="${GTAG_SCRIPT_SRC}"]`)) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = GTAG_SCRIPT_SRC;
  document.head.appendChild(script);
}

export default function AnalyticsProvider({ children }: Props) {
  const { activeOffers, promoCodes, smartPricingDeals } = useDeals();
  const hasDeals = Boolean(activeOffers?.length || promoCodes?.length || smartPricingDeals?.length);
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { company, franchise } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  // The persisted redux copy of the cookie-banner answer. It can outlive the consent cookie (see
  // readCookieConsent), so it is only the signal to read the cookie again (the banner and CookieConsentSync
  // update it); every consent decision below is made on the cookie's answer.
  const { cookiesConsent: persistedCookiesConsent } = useSelector((state: StoreInterface) => state.appStatus);
  const cookiesConsent = useMemo(() => readCookieConsent(), [persistedCookiesConsent]);
  const { isMobileApp } = useMobileApp();

  const { getItem, setItem } = useLocalStorage();

  // GDPR: nothing may initialise or send until the visitor explicitly accepts the cookie banner.
  // Undecided (undefined) and declined ('false') are treated the same. The mobile app WebView never shows
  // the banner and is always tracked (see hasTrackingConsent).
  const hasConsent = hasTrackingConsent(cookiesConsent, isMobileApp);
  // What the platform Mixpanel project's opt-in follows: the app counts as an accept, like hasConsent.
  const readTrackingConsent = (): CookiesConsent => (isMobileApp ? 'true' : readCookieConsent());

  // The provider's own project when it has a key, else FoodAmigos' platform project (see resolveMixpanelProject).
  const mixpanelProject = useMemo(
    () => resolveMixpanelProject(company?.service_provider),
    [company?.service_provider?.mixpanel_api_key, company?.service_provider?.platform_mixpanel_token],
  );
  const mixpanelToken = mixpanelProject?.token ?? null;

  // Session replay config (all env-driven so it can be killed/tuned without a code change).
  const replay = useMemo(() => getReplayConfig(), []);
  // Native white-label WebView is out of scope; consent must be explicitly granted. Replay only ever
  // records into the provider's own project: on the platform fallback a white-label shop's consumers
  // would otherwise be recorded into FoodAmigos' project.
  const replayAllowed =
    replay.enabled && !isMobileApp && cookiesConsent === 'true' && mixpanelProject?.isOwnProject === true;
  // Guards the begin_checkout boost so we only force-start recording once per session.
  const replayStartedRef = useRef(false);

  useEffect(() => {
    if (!hasConsent) {
      return;
    }

    const userId = profile?.id?.toString()
      ? `user-${profile.id}`
      : (getItem(LocalStorageReservedKey.AnalyticsUserId) ?? uuidv4());

    setItem(LocalStorageReservedKey.AnalyticsUserId, userId);

    if (company?.service_provider?.amplitude_api_key) {
      amplitude.init(company?.service_provider?.amplitude_api_key, userId, {
        autocapture: false,
      });
    }
  }, [company?.service_provider?.amplitude_api_key, profile?.id, hasConsent]);

  useEffect(() => {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(MP_DISTINCT_ID_KEY)) {
      return;
    }

    sessionStorage.setItem(MP_DISTINCT_ID_KEY, url.searchParams.get(MP_DISTINCT_ID_KEY));

    url.searchParams.delete(MP_DISTINCT_ID_KEY);

    window.history.replaceState({}, '', url);
  }, []);

  useEffect(() => {
    if (!hasConsent || !mixpanelToken) {
      return;
    }

    const isAuthenticated = !!profile?.id;

    mixpanel?.init(mixpanelToken, {
      api_host: 'https://api-eu.mixpanel.com',
      autocapture: false,
      // Baseline sampling; sessions reaching begin_checkout are force-recorded (see trackBeginCheckout).
      record_sessions_percent: replayAllowed ? replay.baseSamplePct : 0,
      // Mask all form fields, payment iframes, and any node tagged as PII.
      record_mask_all_inputs: true,
      // Belt and braces: the `mp-block` class on our own containers covers the elements we render,
      // and the src matches catch a payment iframe mounted anywhere else. Spreedly serves its
      // card-number/CVV and 3DS challenge frames from core.spreedly.com.
      record_block_selector:
        '.mp-block, iframe[src*="stripe"], iframe[src*="js.stripe"], iframe[src*="adyen"], iframe[src*="spreedly"], iframe[src*="core.spreedly"]',
      record_mask_text_selector: '.mp-mask',
      // No personal data or secret in any event. The URLs the SDK attaches on its own can carry a login
      // token, a payment client secret or an email, so their query is allowlisted (see scrubUrl) before
      // the event is queued, and once more before it is sent.
      hooks: { before_track: scrubUrlsBeforeTrack(mixpanel), before_send_events: scrubUrlsBeforeSend },
      property_blacklist: URL_DERIVED_PROPERTY_BLACKLIST,
      debug: import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD,
      // The platform fallback starts opted out and stores nothing until cookies are accepted (see
      // applyPlatformConsent), and never persists UTM values (PLATFORM_PROJECT_UTM_CONFIG). A provider's own
      // project keeps its behaviour from before.
      ...(mixpanelProject?.isOwnProject ? {} : { ...PLATFORM_PROJECT_CONSENT_CONFIG, ...PLATFORM_PROJECT_UTM_CONFIG }),
      loaded: (mp) => {
        // The SDK sends its first data right after this callback: apply the consent answer the page loaded
        // with first, and scrub the first-touch referrer it sends to the people profile. The answer comes from
        // the consent cookie: the redux copy is persisted and may still say 'true' after the cookie is gone.
        isConsentGated(mp) && applyPlatformConsent(mp, readTrackingConsent());
        sanitizeInitialReferrer(mp);

        const distinctId = sessionStorage.getItem(MP_DISTINCT_ID_KEY);

        distinctId && mp.identify(distinctId);
        distinctId && sessionStorage.removeItem(MP_DISTINCT_ID_KEY);
      },
    });

    if (!isAuthenticated) {
      return;
    }

    if (mixpanel?.get_distinct_id() === profile?.id?.toString()) {
      return;
    }

    mixpanel?.identify(profile?.id?.toString());
  }, [mixpanelToken, profile?.id, replayAllowed, replay.baseSamplePct, hasConsent]);

  // Sentry error replay follows the banner's accept (it is only initialised in production). Unlike event
  // tracking, the mobile app WebView does not count as an accept here.
  useEffect(() => {
    if (cookiesConsent === 'true') {
      enableSentryReplay();

      return;
    }

    disableSentryReplay();
  }, [cookiesConsent]);

  // WLP super properties + groups (partner_id, restaurant_id, restaurant_source, actor_role, is_test)
  // on every Mixpanel event. Declared after the init effect so it runs after init in the same commit.
  // profile?.id is a dependency because AuthProvider's logout calls mixpanel.reset(), which wipes
  // every registered property; the profile clearing re-runs this and puts them back. hasConsent is one
  // because init only happens once consent arrives, which can be long after the company loaded.
  const superProperties = useMemo(
    () => buildConsumerSuperProperties(company),
    [
      company?.id,
      company?.restaurant_source,
      company?.service_provider?.id,
      company?.service_provider?.is_master,
      company?.service_provider?.is_test,
    ],
  );

  useEffect(() => {
    // Not on a withdrawal either: register() would write the mp_ cookie straight back.
    if (!hasConsent || !mixpanelToken || !mixpanel?.['__loaded']) {
      return;
    }

    applyConsumerSuperProperties(mixpanel, superProperties);
  }, [mixpanelToken, superProperties, profile?.id, hasConsent]);

  // Platform fallback: follow the cookie banner as it changes (init's `loaded` applied the answer the page
  // loaded with). The answer is read from the consent cookie, never from the persisted redux copy alone: a
  // change of the copy (the banner, CookieConsentSync on every route) is the signal to read it again.
  // Opting in re-applies the super properties and groups, which the SDK ignores while opted out. A
  // provider's own project is not consent-gated, so this never touches it.
  useEffect(() => {
    if (!isConsentGated(mixpanel)) {
      return;
    }

    if (applyPlatformConsent(mixpanel, readTrackingConsent())) {
      applyConsumerSuperProperties(mixpanel, superProperties);
    }
  }, [mixpanelToken, cookiesConsent]);

  // Respect consent withdrawal: stop any in-flight recording once the cookie no longer says 'true' — a
  // decline, or the consent cookie gone mid-session — matching replayAllowed.
  useEffect(() => {
    if (!mixpanel?.['__loaded']) {
      return;
    }

    if (cookiesConsent !== 'true') {
      mixpanel.stop_session_recording?.();
      replayStartedRef.current = false;
    }
  }, [cookiesConsent]);

  useEffect(() => {
    if (!hasConsent) {
      return;
    }

    loadGtagScript();

    window.gtag('config', franchise?.google_analytics_id ?? company?.service_provider?.google_analytics_id, {
      user_id: profile?.id,
      debug_mode: import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD,
    });
  }, [profile?.id, franchise?.google_analytics_id, company?.service_provider?.google_analytics_id, hasConsent]);

  // React runs effects child-first, so a consumer reacting to consent (MenuProvider's view_menu)
  // would otherwise call in the same commit consent flipped or the SDK keys arrived, before the init
  // effects above ran: Mixpanel drops it (`__loaded` is false), the Pixel gets it before
  // `ReactPixel.init` (TrackingProvider) and gtag gets `event` before `config`. Readiness is keyed on
  // the config it was reached for and only set by an effect declared after the inits, so it lags one
  // commit behind any change.
  const trackingConfigKey = hasConsent
    ? [
        company?.id,
        company?.service_provider?.mixpanel_api_key,
        company?.service_provider?.amplitude_api_key,
        company?.service_provider?.google_analytics_id,
        franchise?.google_analytics_id,
        franchise?.facebook_pixel_id,
      ].join('|')
    : null;
  const [readyTrackingConfigKey, setReadyTrackingConfigKey] = useState<string | null>(null);
  const isTrackingReady = trackingConfigKey !== null && readyTrackingConfigKey === trackingConfigKey;

  useEffect(() => {
    setReadyTrackingConfigKey(trackingConfigKey);
  }, [trackingConfigKey]);

  const trackEvent = async (event: TrackableEvent, data?: object, options?: { sendImmediately?: boolean }) => {
    if (!hasConsent) {
      return;
    }

    window.gtag('event', event, data);

    if (franchise?.facebook_pixel_id) {
      ReactPixel.trackCustom(event, data);
    }

    amplitude.track(event, data);

    if (mixpanel['__loaded']) {
      mixpanel.track(
        event,
        data,
        options?.sendImmediately ? { send_immediately: true, transport: 'sendBeacon' } : undefined,
      );
    }

    if (options?.sendImmediately) {
      amplitude.flush();
    }
  };

  const trackOfferImpression = async (props: DealEventProps) => trackEvent(TrackableEvent.OfferImpression, props);

  const trackOfferClicked = async (props: DealEventProps) => trackEvent(TrackableEvent.OfferClicked, props);

  const trackOfferApplied = async (props: DealEventProps) => trackEvent(TrackableEvent.OfferApplied, props);

  // Offers are auto-applied to the cart (see useAutoApplyBestOffer) and re-applied on
  // every cart mutation. Fire offer_applied once per session per offer when an offer
  // first enters the applied set.
  const appliedOffers = useSelector(selectAppliedOffers);

  useEffect(() => {
    // Without consent, bail before markOnce so the offer still counts once tracking starts.
    if (!hasConsent || !company?.id) {
      return;
    }

    const ctx = { restaurantId: company.id, isLoggedIn: !!profile?.id };

    appliedOffers?.forEach((offer) => {
      if (markOnce(DEDUPE_NAMESPACES.Applied, `offer:${offer.id}`)) {
        trackOfferApplied(offerEventProps(offer, ctx));
      }
    });
  }, [appliedOffers, company?.id, profile?.id, hasConsent]);

  // welcome_bonus / mobile_signup convert on signup. AuthProvider stashes the bonus
  // kind (it sits above AnalyticsProvider); consume it once the user is identified.
  useEffect(() => {
    // Without consent the pending bonus stays stashed, so it is still reported if the visitor accepts.
    if (!hasConsent || !profile?.id || !company?.id) {
      return;
    }

    let pendingKind: string | null = null;
    try {
      pendingKind = sessionStorage.getItem(PENDING_BONUS_KEY);
    } catch {
      pendingKind = null;
    }

    if (!pendingKind) {
      return;
    }

    try {
      sessionStorage.removeItem(PENDING_BONUS_KEY);
    } catch {
      // ignore
    }

    const ctx = { restaurantId: company.id, isLoggedIn: true };
    const cashbackProgram = company?.cashback_program;

    if (pendingKind === LoginBonusKind.WelcomeBonus) {
      trackOfferApplied(bonusEventProps(OfferEventType.WelcomeBonus, cashbackProgram?.welcome_bonus_image_url, ctx));
    }

    if (pendingKind === LoginBonusKind.MobileAppSignupBonus) {
      trackOfferApplied(bonusEventProps(OfferEventType.MobileSignup, cashbackProgram?.app_signup_bonus_image_url, ctx));
    }
  }, [profile?.id, company?.id, hasConsent]);

  const trackViewItem = async (product: CompiledProduct) =>
    trackEvent(TrackableEvent.ViewItem, {
      company_id: company?.id,
      currency: company?.currency,
      value: product?.actual_price,
      order_method: cached_order?.method,
      delivery_mode: company?.delivery_mode,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          price: product?.actual_price,
          location_id: company?.google_places_id,
        },
      ],
    });

  const trackViewMenu = useCallback(
    (menuCategories: IMap<CategoryInterface>) => {
      // Until the SDKs are initialised, bail before setting the once-per-session flag so view_menu
      // still fires once they are (see isTrackingReady).
      if (!isTrackingReady || !company?.id || !franchise?.id) {
        return;
      }

      const isViewMenuTrackedKey = IS_VIEW_MENU_TRACKED_KEY.concat(company.id.toString());

      if (sessionStorage.getItem(isViewMenuTrackedKey) === 'true') {
        return;
      }

      sessionStorage.setItem(isViewMenuTrackedKey, 'true');

      const menuCategoriesCollection = collect(Object.values(menuCategories));

      return trackEvent(TrackableEvent.ViewMenu, {
        company_id: company.id,
        franchise_id: franchise.id,
        menu_size: menuCategoriesCollection.pluck('product_ids').collapse().unique().count() ?? 0,
        categories_count: menuCategoriesCollection.count() ?? 0,
        has_offers_and_promotions: hasDeals,
      });
    },
    [company?.id, franchise?.id, hasDeals, isTrackingReady],
  );

  const trackAddToBasket = async (item: CompleteBasketItem) =>
    trackEvent(TrackableEvent.AddToBasket, {
      company_id: company?.id,
      currency: company?.currency,
      value: item?.actual_price,
      order_method: cached_order?.method,
      delivery_mode: company?.delivery_mode,
      items: [
        {
          item_id: item.product_id,
          item_name: item.entry?.name,
          price: item?.actual_price / item.quantity,
          quantity: item.quantity,
          location_id: company?.google_places_id,
        },
      ],
    });

  // Fired when an add-to-basket tap resolves to a no-op (no dispatch). The success event
  // only fires after a dispatch, so without this the failure mode is invisible in analytics.
  const trackAddToBasketFailed = async (params: { reason: string; product_id?: number; message?: string }) =>
    trackEvent(TrackableEvent.AddToBasketFailed, {
      company_id: company?.id,
      order_method: cached_order?.method,
      ...params,
    });

  const trackRemoveFromBasket = async (item: CompleteBasketItem) =>
    trackEvent(TrackableEvent.RemoveFromBasket, {
      company_id: company?.id,
      currency: company?.currency,
      value: item?.actual_price,
      order_method: cached_order?.method,
      delivery_mode: company?.delivery_mode,
      items: [
        {
          item_id: item.product_id,
          item_name: item.entry?.name,
          price: item?.actual_price / item.quantity,
          quantity: item.quantity,
          location_id: company?.google_places_id,
        },
      ],
    });

  const trackBeginCheckout = async (order: Order, lineItems: CompleteBasketItem[]) => {
    const result = trackEvent(TrackableEvent.BeginCheckout, {
      company_id: company?.id,
      value: order.total,
      currency: company?.currency,
      order_method: order.method,
      delivery_mode: company?.delivery_mode,
      // No payment method is chosen yet at begin_checkout, so this can only be the primary gateway —
      // the one that processes every method except PayPal-via-Stripe, PayPal-direct and cash.
      // begin_purchase and complete_purchase carry the gateway that actually processes the payment.
      payment_gateway: company?.payment_gateways?.[0],
      primary_payment_gateway: company?.payment_gateways?.[0] ?? null,
      payment_method: order.payment?.payment_method_id,
      items: lineItems.map((item) => ({
        item_id: item.product_id,
        item_name: item.entry?.name,
        price: item.actual_price / item.quantity,
        quantity: item.quantity,
        location_id: company?.google_places_id,
      })),
    });

    // Force-record sessions that reach checkout (independent of the baseline sample).
    // Gate on `__loaded`: for companies without any Mixpanel token, init() never runs, so the
    // instance's recorderManager is undefined and start_session_recording() would throw
    // (an unhandled rejection, since trackBeginCheckout is fire-and-forget). The try/catch
    // is defence-in-depth — analytics must never break the checkout flow.
    if (
      mixpanel?.['__loaded'] &&
      replayAllowed &&
      !replayStartedRef.current &&
      shouldRecordPercent(replay.checkoutSamplePct)
    ) {
      try {
        mixpanel.start_session_recording?.();
        replayStartedRef.current = true;
      } catch {
        // recorder unavailable — recording is best-effort, don't interrupt checkout
      }
    }

    return result;
  };

  // payment_gateway = the gateway that processes the charge, primary_payment_gateway = the company's
  // primary one: the same pair the backend's order_placed carries, so the funnel splits consistently.
  const trackBeginPurchase = async (order: Order, lineItems: CompleteBasketItem[], extra?: BeginPurchaseExtra) =>
    trackEvent(
      TrackableEvent.BeginPurchase,
      {
        company_id: company?.id,
        value: order.total,
        currency: company?.currency,
        order_method: order.method,
        delivery_mode: company?.delivery_mode,
        payment_gateway: resolveAnalyticsPaymentGateway(
          extra?.payment_method ?? order.payment?.payment_method_id,
          company?.payment_gateways,
          company?.supported_payment_methods,
        ),
        primary_payment_gateway: company?.payment_gateways?.[0] ?? null,
        payment_method: order.payment?.payment_method_id,
        items: lineItems.map((item) => ({
          item_id: item.product_id,
          item_name: item.entry?.name,
          price: item.actual_price / item.quantity,
          quantity: item.quantity,
          location_id: company?.google_places_id,
        })),
        ...(extra ?? {}),
      },
      { sendImmediately: true },
    );

  const trackCompletePurchase = async (payment: Payment, order: Order) => {
    // The GA and Meta conversions below bypass trackEvent, so they need the gate too.
    // Cash and pay-at-pickup orders have no payment yet.
    if (!hasConsent || !payment) {
      return;
    }

    trackGoogleAnalyticsConversion(payment, order);
    trackFacebookPixelConversion(payment, order);

    // The order's own transaction says how it was charged (`paypal_standalone` for PayPal-direct,
    // `paypal` for PayPal via Stripe), so the gateway is resolved from it, not from the checkout UI.
    const gateways = order.company?.payment_gateways ?? company?.payment_gateways;
    const supportedMethods = order.company?.supported_payment_methods ?? company?.supported_payment_methods;

    trackEvent(TrackableEvent.CompletePurchase, {
      company_id: order.company?.id,
      transaction_id: payment.id,
      value: payment.amount,
      currency: order.company?.currency,
      order_method: order.method,
      delivery_mode: company?.delivery_mode,
      payment_gateway: resolveAnalyticsPaymentGateway(order.payment?.payment_method_id, gateways, supportedMethods),
      primary_payment_gateway: gateways?.[0] ?? null,
      payment_method: order.payment?.payment_method_id,
      applied_offer_ids: (order.applied_offers ?? []).map((offer) => `offer:${offer.id}`),
      items: order.line_items.map((item) => ({
        item_id: item.product_id,
        item_name: item.name,
        price: item.actual_price / item.quantity,
        quantity: item.quantity,
        location_id: company?.google_places_id,
      })),
    });
  };

  const trackOtpRequested = async (params: { otp_channel: OTP_DELIVERY_CHANNELS; channel_is_default: boolean }) =>
    trackEvent(TrackableEvent.OtpRequested, {
      company_id: company?.id,
      ...params,
    });

  const trackOtpResendRequested = async (params: {
    otp_channel: OTP_DELIVERY_CHANNELS;
    switched_on_resend: boolean;
    resend_number: number;
  }) => trackEvent(TrackableEvent.OtpResendRequested, params);

  const trackOtpVerificationFailed = async (params: {
    otp_channel: OTP_DELIVERY_CHANNELS;
    failure_reason: OTP_VERIFICATION_FAILURE_REASON;
    raw_error_code?: string | null;
    status?: number | null;
  }) => trackEvent(TrackableEvent.OtpVerificationFailed, params);

  const trackOtpVerified = async (params: { otp_channel: OTP_DELIVERY_CHANNELS }) =>
    trackEvent(
      TrackableEvent.OtpVerified,
      {
        company_id: company?.id,
        ...params,
      },
      { sendImmediately: true },
    );

  const trackGoogleAnalyticsConversion = async (payment: Payment, order: Order) =>
    franchise?.google_analytics_id &&
    franchise?.google_ads_conversion_label_id &&
    window.gtag('event', TrackableEvent.GA_Conversion, {
      send_to: `${franchise.google_analytics_id}/${franchise.google_ads_conversion_label_id}`,
      value: payment.amount,
      currency: order.company?.currency,
      company_id: order.company?.id,
      transaction_id: payment.id,
      order_method: order.method,
      delivery_mode: order.company?.delivery_mode,
      items: order.line_items.map((item) => ({
        item_id: item.product_id,
        item_name: item.name,
        price: item.actual_price / item.quantity,
        quantity: item.quantity,
        location_id: order.company?.google_places_id,
      })),
    });

  const trackFacebookPixelConversion = async (payment: Payment, order: Order) =>
    franchise?.facebook_pixel_id &&
    // eventID must equal the event_id the backend sends via the Conversions API (the order uuid),
    // otherwise Meta counts the browser and server Purchase as two conversions.
    ReactPixel.fbq(
      'track',
      TrackableEvent.FB_Purchase,
      { value: payment.amount, currency: order.company?.currency },
      { eventID: order.uuid },
    );

  const context = useMemo(
    () => ({
      trackViewItem,
      trackAddToBasket,
      trackAddToBasketFailed,
      trackRemoveFromBasket,
      trackBeginCheckout,
      trackBeginPurchase,
      trackCompletePurchase,
      trackViewMenu,
      trackOtpRequested,
      trackOtpResendRequested,
      trackOtpVerificationFailed,
      trackOtpVerified,
      trackOfferImpression,
      trackOfferClicked,
      trackOfferApplied,
    }),
    [cached_order, trackViewMenu, company?.id, hasConsent],
  );

  return <AnalyticsContext.Provider value={context}>{children}</AnalyticsContext.Provider>;
}
